import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BadgeCheck,
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  FileUp,
  MessageSquare,
  Printer,
  Trash2,
  XCircle,
} from 'lucide-react';
import type { RfqHubFileRelease, RfqPackage, RfqSubmission, TimberSupplierStructureRef, VendorFileRelease } from '../../types';
import VendorPortalVersionTree from './VendorPortalVersionTree';
import VendorPortalProjectBadges from './VendorPortalProjectBadges';
import VendorPortalQuotesByVersionPanel, {
  buildVendorPortalVersionHistoryRows,
} from './VendorPortalQuotesByVersionPanel';
import { cncAssignmentChipLabel, cncMaterialFlowLabel } from '../../utils/cncRfqAssignments';
import { formatQuoteTotal, submissionQuoteTotal } from '../../utils/rfqQuoteTotal';
import {
  TIMBER_SUPPLIER_SURFACE_SPEC,
  timberSupplierDimensionsSpecForLists,
  timberSupplierExportOptionsForPackage,
} from '../../utils/supplierTimberExport';
import {
  rfqProjectSupplierShipTo,
  shipToContextForWorkType,
  vendorPortalDeliveryAddress,
  type ProjectSiteAddressInput,
} from '../../utils/supplierShipTo';
import SupplierShipToLine from './SupplierShipToLine';
import {
  buildVendorPortalTimberQuoteHeadings,
  buildVendorPortalTimberRequestedBadges,
  isActiveTimberQuoteSubmission,
} from '../../utils/vendorPortalTimberQuotes';
import {
  cncSubmissionHistoryKey,
  groupQuoteSubmissionHistory,
} from '../../utils/rfqSubmissionHistory';
import { timberQuoteStructureScopeLabel } from '../../utils/timberQuoteStreamKey';
import { packageHubReleaseIds } from '../../utils/rfqPackageReleases';
import { isReferenceRelease } from '../../utils/rfqReleasePurpose';
import { vendorPackageStructureSlots } from '../../utils/vendorPortalStructureSlots';
import { speciesForStructure } from '../../utils/timberRfqStructureSpecies';
import {
  approvedSubmissionIdsForPackage,
  approvedPackageIdsForWorkGroup,
  cncAwardedSubmissionIdsForPackage,
  cncPackageApprovedBadgeLabel,
  packageApprovedBadgeLabel,
  timberSubmissionApprovedBadgeLabel,
  workGroupHasApprovedQuote,
  workGroupHasStaffRejectedQuote,
  workGroupIsAwardedElsewhere,
  workGroupIsLostAward,
} from '../../utils/vendorPortalApprovals';
import { isRfqQuoteRejected } from '../../utils/rfqQuoteRejectionTargets';
import {
  PENDING_REVISION_HINT,
  resolveQuoteStreamDisplay,
  visibleQuoteRowsNewestFirst,
} from '../../utils/vendorPortalQuoteDisplay';
import {
  buildVendorPackageVersions,
  projectNeedsQuoteResponse,
  resolveVendorVersionRow,
  vendorPackageMainHeading,
  vendorPackageMainHeadingIsPrice,
  vendorPackageQuoteStatusLabel,
  vendorPackageVersionLabel,
  vendorPackageVersionLabelForPackage,
} from '../../utils/vendorPackageVersion';
import { threeDDownloadsForPackage } from '../../utils/vendorFileReleaseService';
import { formatSubmissionLeadTimeWeeks } from '../../utils/rfqLeadTimeWeeks';
import { normalizeWorkType } from '../../utils/vendorHelpers';
import { vendorSupersedesInfo, type VendorPortalWorkGroup } from '../../utils/vendorPortalDealGroups';
import {
  isPrePortalPackage,
  isQuotePlaceholderPackage,
  PRE_PORTAL_VENDOR_PORTAL_NO_FILES_HINT,
} from '../../utils/timberRfqPrePortalVersion';
import {
  CNC_QUOTE_PLACEHOLDER_ON_FILE_LABEL,
  isQuotePlaceholderSubmission,
  QUOTE_PLACEHOLDER_VENDOR_PORTAL_NO_3D_HINT,
} from '../../utils/combinedQuoteSplit';
import './VendorPortalVersionStrip.css';

export type VendorPortalWorkPanelProps = {
  sectionTitle: string;
  work: VendorPortalWorkGroup;
  orgId: string | undefined;
  supplierOrgName?: string;
  projectSite?: ProjectSiteAddressInput;
  /** Sibling work type on the same deal (timber ↔ CNC) for delivery address resolution. */
  siblingWork?: VendorPortalWorkGroup | null;
  submissionsByPackage: Map<string, RfqSubmission[]>;
  vendorReleases: VendorFileRelease[];
  hubReleaseById?: Map<string, RfqHubFileRelease>;
  selectedPackageId: string | null;
  onSelectPackage: (packageId: string) => void;
  busyPkg: string | null;
  readOnlyPreview: boolean;
  /** When false, hide quote money, submit, and not-bidding (supplier employees). */
  canSeeFinancials?: boolean;
  userId?: string;
  /** Download the supplier's CSV / PDF — the whole package, or one structure when given. */
  onDownloadCsv: (pkg: RfqPackage, structure?: TimberSupplierStructureRef) => void;
  onDownloadPdf: (pkg: RfqPackage, structure?: TimberSupplierStructureRef) => void;
  onDownloadThreeDFile: (pkg: RfqPackage) => void;
  onNotBidding: (pkg: RfqPackage) => void;
  /** `structure` set → the upload is tagged to that structure only (multi-structure packages). */
  onUploadQuote: (pkg: RfqPackage, structure?: TimberSupplierStructureRef) => void;
  onViewSubmission: (sub: RfqSubmission) => void;
  onDeleteSubmission: (pkg: RfqPackage, sub: RfqSubmission) => void;
  underContract?: boolean;
  showProductionComments?: boolean;
  onOpenProductionComments?: () => void;
};

function PrePortalLockedButton({
  label,
  icon,
  className = 'vp-btn',
  tip = PRE_PORTAL_VENDOR_PORTAL_NO_FILES_HINT,
}: {
  label: string;
  icon?: React.ReactNode;
  className?: string;
  tip?: string;
}) {
  return (
    <span className="vp-btn-tip-wrap" title={tip}>
      <button
        type="button"
        className={`${className} vp-btn--pre-portal-locked`}
        disabled
        aria-disabled="true"
      >
        {icon}
        {label}
      </button>
    </span>
  );
}

const VendorPortalWorkPanel: React.FC<VendorPortalWorkPanelProps> = ({
  sectionTitle,
  work,
  orgId,
  supplierOrgName = 'Supplier',
  projectSite = {},
  siblingWork,
  submissionsByPackage,
  vendorReleases,
  hubReleaseById,
  selectedPackageId,
  onSelectPackage,
  busyPkg,
  readOnlyPreview,
  canSeeFinancials = true,
  userId,
  onDownloadCsv,
  onDownloadPdf,
  onDownloadThreeDFile,
  onNotBidding,
  onUploadQuote,
  onViewSubmission,
  onDeleteSubmission,
  underContract = false,
  showProductionComments = false,
  onOpenProductionComments,
}) => {
  const [expandedHistoryIds, setExpandedHistoryIds] = useState<Set<string>>(new Set());
  const [quotesPopoverOpen, setQuotesPopoverOpen] = useState(false);
  const quotesPopoverRef = useRef<HTMLDivElement>(null);
  const toggleHistory = (id: string) => {
    setExpandedHistoryIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const versionNameContext = { hubReleaseById, vendorReleases, orgId };

  useEffect(() => {
    if (!quotesPopoverOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (quotesPopoverRef.current?.contains(event.target as Node)) return;
      setQuotesPopoverOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [quotesPopoverOpen]);

  const { project, packages } = work;
  const versions = buildVendorPackageVersions(packages, submissionsByPackage);
  const selectedRow = resolveVendorVersionRow(versions, selectedPackageId, packages);
  const pkg = selectedRow?.pkg;

  const approvedVersionPackageIds = approvedPackageIdsForWorkGroup(
    work,
    submissionsByPackage,
    orgId,
  );

  const approvedPackageBadgeLabels = useMemo(() => {
    const map = new Map<string, string>();
    if (!orgId) return map;
    for (const row of versions) {
      const id = row.pkg.id;
      if (!id || !approvedVersionPackageIds.has(id)) continue;
      map.set(
        id,
        packageApprovedBadgeLabel(project, row.pkg, packages, submissionsByPackage, orgId),
      );
    }
    return map;
  }, [versions, project, packages, submissionsByPackage, orgId, approvedVersionPackageIds]);

  const approvedSubmissionIds = useMemo(() => {
    if (!pkg?.id || !selectedRow) return new Set<string>();
    if (normalizeWorkType(pkg.workType) === 'cnc_quote') {
      return cncAwardedSubmissionIdsForPackage(
        project,
        pkg,
        orgId,
        selectedRow.submissions,
      );
    }
    return approvedSubmissionIdsForPackage(project, pkg, packages, submissionsByPackage);
  }, [pkg, selectedRow, project, packages, submissionsByPackage, orgId]);

  const supplierQuoteRows = useMemo(() => {
    if (!pkg?.id || !selectedRow) return [];
    const rowSubs = selectedRow.submissions;
    const rowIsCnc = normalizeWorkType(pkg.workType) === 'cnc_quote';
    const rowApprovedIds = approvedSubmissionIds;
    if (rowIsCnc) {
      return groupQuoteSubmissionHistory(
        rowSubs.filter(isActiveTimberQuoteSubmission),
        cncSubmissionHistoryKey,
      ).map(group => {
        const display = resolveQuoteStreamDisplay(group, rowApprovedIds);
        const hideCarveOut =
          isQuotePlaceholderPackage(pkg)
          || isQuotePlaceholderSubmission(display.primary);
        return {
          sub: display.primary,
          priceLabel: hideCarveOut
            ? CNC_QUOTE_PLACEHOLDER_ON_FILE_LABEL
            : formatQuoteTotal(submissionQuoteTotal(display.primary)),
          species: null as string | null,
          drynessLabel: null as string | null,
          structureScopeLabel: timberQuoteStructureScopeLabel(display.primary),
          previous: display.previous,
          pendingRevisions: display.pendingRevisions,
        };
      });
    }
    return buildVendorPortalTimberQuoteHeadings(rowSubs, project, orgId ?? '', rowApprovedIds)
      .filter(h => h.submission.storagePath)
      .map(h => ({
        sub: h.submission,
        priceLabel: h.priceLabel,
        species: h.species,
        drynessLabel: h.drynessLabel,
        structureScopeLabel: h.structureScopeLabel,
        previous: h.previous,
        pendingRevisions: h.pendingRevisions,
      }));
  }, [pkg, selectedRow, project, orgId, approvedSubmissionIds]);

  const supplierQuoteEntries = useMemo(() => {
    const rowIsCnc = !!pkg && normalizeWorkType(pkg.workType) === 'cnc_quote';
    const entries = supplierQuoteRows.flatMap(
      ({ sub, priceLabel, species, drynessLabel, structureScopeLabel, previous, pendingRevisions }) =>
        visibleQuoteRowsNewestFirst(
          {
            primary: sub,
            pendingRevisions,
            previous,
            hasApprovedPrimary:
              pendingRevisions.length > 0
              || (!!sub.id && approvedSubmissionIds.has(sub.id)),
          },
          approvedSubmissionIds,
        ).map(row => ({
          ...row,
          priceLabel:
            row.sub === sub
              ? priceLabel
              : rowIsCnc && isQuotePlaceholderSubmission(row.sub)
                ? CNC_QUOTE_PLACEHOLDER_ON_FILE_LABEL
                : formatQuoteTotal(submissionQuoteTotal(row.sub)),
          species,
          drynessLabel,
          structureScopeLabel,
        })),
    );
    return entries.sort((a, b) => {
      const ta = a.sub.createdAt instanceof Date ? a.sub.createdAt.getTime() : 0;
      const tb = b.sub.createdAt instanceof Date ? b.sub.createdAt.getTime() : 0;
      return tb - ta;
    });
  }, [supplierQuoteRows, approvedSubmissionIds, pkg]);

  const versionHistoryRows = useMemo(
    () =>
      buildVendorPortalVersionHistoryRows(
        versions,
        project,
        packages,
        submissionsByPackage,
        orgId,
        versionNameContext,
      ),
    [versions, project, packages, submissionsByPackage, orgId, hubReleaseById, vendorReleases],
  );
  const showQuotesByVersion = versions.length > 1;

  if (!pkg?.id || !selectedRow) return null;

  const subs = selectedRow.submissions;
  const isCnc = normalizeWorkType(pkg.workType) === 'cnc_quote';
  const dimensionsSpec = timberSupplierDimensionsSpecForLists(
    packageHubReleaseIds(pkg)
      .map((id) => hubReleaseById?.get(id))
      .filter((release): release is RfqHubFileRelease => !!release)
      .map((release) => ({
        trueDimensions: release.trueDimensions === true,
        label: release.displayLabel || release.fileName,
      })),
  );
  // Reference release: refined file after the award — cut from it, do not requote.
  const isReferencePkg = isReferenceRelease(pkg);
  const referenceStructureNames = (pkg.scopeStructures ?? [])
    .map(s => (s.name || '').trim())
    .filter(Boolean);
  const supersedes = vendorSupersedesInfo(pkg, packages, submissionsByPackage);
  const hasApprovedQuote = workGroupHasApprovedQuote(work, submissionsByPackage, orgId);
  const hasLostAward = workGroupIsLostAward(work, submissionsByPackage, orgId);
  const quoteRejected = workGroupHasStaffRejectedQuote(work, submissionsByPackage, orgId);
  const awardedElsewhere = workGroupIsAwardedElsewhere(work, submissionsByPackage, orgId);
  const cncAssignments = isCnc
    ? (pkg.cncPackagePayload?.speciesAssignments ?? []).filter(a => a.species?.trim())
    : [];
  const cncStructureNames = isCnc
    ? (pkg.cncPackagePayload?.structures ?? []).map(s => s.name.trim()).filter(Boolean)
    : [];
  const hubReleases: RfqHubFileRelease[] = [];
  hubReleaseById?.forEach(release => {
    hubReleases.push(release);
  });
  const threeDDownloads = threeDDownloadsForPackage(
    pkg,
    vendorReleases,
    hubReleases,
    orgId,
  );
  const hasThreeDFile = threeDDownloads.length > 0;
  const needsQuote = projectNeedsQuoteResponse(packages, submissionsByPackage, project.status);
  const isPrePortal = isPrePortalPackage(pkg);
  const isQuotePlaceholder = isQuotePlaceholderPackage(pkg);
  const lockedFilesTip = isQuotePlaceholder
    ? QUOTE_PLACEHOLDER_VENDOR_PORTAL_NO_3D_HINT
    : PRE_PORTAL_VENDOR_PORTAL_NO_FILES_HINT;
  const latestSub = subs[0];
  const timberQuoteHeadings =
    !isCnc && orgId
      ? buildVendorPortalTimberQuoteHeadings(subs, project, orgId, approvedSubmissionIds)
      : [];
  const timberRequestedBadges =
    !isCnc && orgId ? buildVendorPortalTimberRequestedBadges(project, orgId) : [];
  const headingIsPrice = vendorPackageMainHeadingIsPrice(selectedRow);
  const mainHeadingIsPrice = canSeeFinancials && headingIsPrice;
  const mainHeading =
    !canSeeFinancials && headingIsPrice
      ? vendorPackageQuoteStatusLabel(selectedRow.quoteStatus)
      : vendorPackageMainHeading(selectedRow);
  const cncApprovedLabel = cncPackageApprovedBadgeLabel(project, pkg, orgId);
  const showTimberQuoteHeader =
    !isCnc && timberQuoteHeadings.length > 0 && supplierQuoteRows.length === 0;
  // A package covering several structures (one RFQ per supplier from Files → RFQs, or a
  // multi-structure CNC scope) gets one upload slot per structure.
  const structureSlots = vendorPackageStructureSlots(project, orgId, pkg, subs);
  const quotedSlotCount = structureSlots.filter(sl => sl.latest).length;
  // Multi-structure timber packages download per structure only — the whole-package
  // CSV / PDF / 3D buttons go away so there is one obvious list per structure.
  const perStructureDownloads = !isCnc && !isPrePortal && structureSlots.length > 1;
  // Species the supplier prices on each structure, when the request narrows them per structure.
  const publishedExportOptions = !isCnc && orgId
    ? timberSupplierExportOptionsForPackage(project, pkg)
    : undefined;
  const speciesLabelFor = (structure: TimberSupplierStructureRef): string | null => {
    const rules = publishedExportOptions?.structureSpecies ?? [];
    if (!rules.length) return null;
    const species = speciesForStructure(
      structure,
      (publishedExportOptions?.requestedSpeciesEntries ?? []).map(entry => entry.species),
      rules,
    );
    return species.length ? species.join(', ') : 'Not priced';
  };

  const shipToContext = {
    ...shipToContextForWorkType(
      normalizeWorkType(pkg.workType),
      normalizeWorkType(pkg.workType) === 'timber_rfq' ? project : siblingWork?.project,
      normalizeWorkType(pkg.workType) === 'cnc_quote' ? project : siblingWork?.project,
    ),
    orgId: orgId ?? '',
    projectSite,
  };
  const liveDeliveryAddress = orgId
    ? rfqProjectSupplierShipTo(project, orgId, shipToContext)
    : null;
  const deliveryAddress = vendorPortalDeliveryAddress(pkg, liveDeliveryAddress);

  // One project can render several panels (one per structure lane) — keep heading ids unique.
  const headingId = `vp-work-${project.id}-${sectionTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  return (
    <section className="vp-work-section" aria-labelledby={headingId}>
      <header className="vp-work-section-head">
        <h2 id={headingId} className="vp-work-section-title">
          {sectionTitle}
        </h2>
        <div className="vp-work-section-head-actions">
          <VendorPortalProjectBadges
            project={project}
            needsQuote={needsQuote && !hasApprovedQuote && !hasLostAward}
            approved={hasApprovedQuote}
            quoteRejected={quoteRejected}
            awardedElsewhere={awardedElsewhere && !quoteRejected}
            underContract={underContract}
          />
          {showProductionComments && onOpenProductionComments && (
            <button
              type="button"
              className="vp-comments-btn"
              onClick={onOpenProductionComments}
              title="Open Production comments"
              aria-label="Open Production comments"
            >
              <MessageSquare size={14} strokeWidth={1.85} aria-hidden />
              Comments
            </button>
          )}
          {showQuotesByVersion && (
            <div className="vp-quotes-popover-wrap" ref={quotesPopoverRef}>
              <button
                type="button"
                className={`vp-quotes-popover-chip${quotesPopoverOpen ? ' open' : ''}`}
                aria-expanded={quotesPopoverOpen}
                aria-haspopup="dialog"
                onClick={() => setQuotesPopoverOpen(open => !open)}
              >
                All quotes
                <ChevronDown size={13} aria-hidden />
              </button>
              {quotesPopoverOpen && (
                <div className="vp-quotes-popover-panel" role="dialog" aria-label="All quotes by version">
                  <p className="vp-version-history-title">Your quotes by version</p>
                  <VendorPortalQuotesByVersionPanel
                    rows={versionHistoryRows}
                    project={project}
                    orgId={orgId}
                    canSeeFinancials={canSeeFinancials}
                    onViewSubmission={sub => {
                      setQuotesPopoverOpen(false);
                      void onViewSubmission(sub);
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      <ul className="vp-pkg-list">
        <li className="vp-pkg">
          <div className="vp-pkg-layout">
            <div className="vp-pkg-layout-tree" data-tour="vp-version-tree">
              <VendorPortalVersionTree
                versions={versions}
                selectedPackageId={selectedPackageId}
                onSelect={onSelectPackage}
                hubReleaseById={hubReleaseById}
                vendorReleases={vendorReleases}
                orgId={orgId}
                approvedPackageBadgeLabels={approvedPackageBadgeLabels}
              />
            </div>
            <div className="vp-pkg-layout-separator" aria-hidden />
            <div className="vp-pkg-layout-main">
              <div className="vp-pkg-top">
                <div className="vp-pkg-content">
                  {isReferencePkg && (
                    <p className="vp-reference-note" role="status">
                      <strong>Reference release — do not requote.</strong> This is the refined{' '}
                      {isCnc ? 'model' : 'timber list'} for the package you already quoted. Cut from this version;
                      your approved pricing stands.
                      <span className="vp-reference-note-covers">
                        Covers:{' '}
                        {referenceStructureNames.length ? referenceStructureNames.join(', ') : 'all structures'}
                      </span>
                    </p>
                  )}
                  {supersedes && !isReferencePkg && (
                    <p className="vp-requote-note" role="status">
                      <strong>Re-quote requested</strong> — {vendorPackageVersionLabelForPackage(selectedRow.pkg)} replaces{' '}
                      {typeof supersedes.previousVersionIndex === 'number'
                        ? vendorPackageVersionLabel(supersedes.previousVersionIndex)
                        : 'your earlier list'}
                      {canSeeFinancials && supersedes.previousQuoteTotal != null ? (
                        <>
                          {' '}
                          (quoted {formatQuoteTotal(supersedes.previousQuoteTotal)}
                          {supersedes.previousQuote ? (
                            <>
                              {' · '}
                              <button type="button" onClick={() => void onViewSubmission(supersedes.previousQuote!)}>
                                open prior quote
                              </button>
                            </>
                          ) : null}
                          )
                        </>
                      ) : null}
                      . Please upload an updated quote for this list.
                    </p>
                  )}
                  {pkg.scopeRevisedAt instanceof Date && (
                    <p className="vp-scope-updated-note">
                      Scope updated{' '}
                      {pkg.scopeRevisedAt.toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}{' '}
                      — same file as before; only the structures to quote changed.
                    </p>
                  )}
                  {showTimberQuoteHeader ? (
                    <ul className="vp-timber-quote-header">
                      {timberQuoteHeadings.map(heading => (
                        <li key={heading.submission.id} className="vp-timber-quote-header-row">
                          {canSeeFinancials && heading.priceLabel && heading.priceLabel !== '—' && (
                            <span className="vp-pkg-title vp-pkg-title--price">{heading.priceLabel}</span>
                          )}
                          {heading.species && (
                            <span className="vp-quote-badge vp-quote-badge--species">{heading.species}</span>
                          )}
                          <span className="vp-quote-badge vp-quote-badge--dryness">{heading.drynessLabel}</span>
                          {heading.structureScopeLabel && (
                            <span className="vp-quote-badge">{heading.structureScopeLabel}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : !isCnc && latestSub?.notBidding && timberQuoteHeadings.length === 0 ? (
                    <div className="vp-pkg-title vp-pkg-title--status">Not bidding</div>
                  ) : !isCnc && hasLostAward && timberQuoteHeadings.length === 0 ? (
                    <div className="vp-pkg-title vp-pkg-title--status">
                      {quoteRejected ? 'Quote rejected' : 'Awarded elsewhere'}
                    </div>
                  ) : !isCnc && timberQuoteHeadings.length === 0 && timberRequestedBadges.length > 0 ? (
                    <div className="vp-timber-awaiting">
                      <div className="vp-pkg-title vp-pkg-title--status">Awaiting quote</div>
                      <ul className="vp-timber-quote-header">
                        {timberRequestedBadges.map(badge => (
                          <li key={badge.species} className="vp-timber-quote-header-row vp-timber-quote-header-row--requested">
                            <span className="vp-quote-badge vp-quote-badge--species">{badge.species}</span>
                            <span className="vp-quote-badge vp-quote-badge--dryness">{badge.drynessLabel}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (!isCnc && supplierQuoteRows.length > 0) || isCnc ? null : (
                    <div
                      className={`vp-pkg-title${mainHeadingIsPrice ? ' vp-pkg-title--price' : ' vp-pkg-title--status'}`}
                    >
                      {mainHeading}
                    </div>
                  )}
                  {isCnc && (cncAssignments.length > 0 || cncStructureNames.length > 0) && (
                    <div className="vp-cnc-assignments">
                      {cncStructureNames.length > 0 && (
                        <p className="vp-cnc-structures">
                          <strong>Structures to quote:</strong> {cncStructureNames.join(', ')}
                        </p>
                      )}
                      <ul className="vp-timber-quote-header">
                        {cncAssignments.map(a => (
                          <li
                            key={a.species}
                            className="vp-timber-quote-header-row vp-timber-quote-header-row--requested"
                          >
                            <span className="vp-quote-badge vp-quote-badge--species">
                              {cncAssignmentChipLabel(a)}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <ul className="vp-cnc-material-flow">
                        {cncAssignments.map(a => (
                          <li key={a.species}>
                            {cncMaterialFlowLabel(a, 'supplier')}
                            {a.note ? ` — ${a.note}` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {!isCnc && (
                    <p className="vp-timber-standards">
                      {dimensionsSpec} · {TIMBER_SUPPLIER_SURFACE_SPEC}
                    </p>
                  )}
                  {deliveryAddress && (
                    <SupplierShipToLine address={deliveryAddress} variant="portal" />
                  )}
                </div>

                <div className="vp-action-groups">
                  <div className="vp-action-group vp-action-group--lctf" data-tour={isCnc ? 'vp-cnc-lctf' : 'vp-timber-lctf'}>
                    <p className="vp-action-group-label">From LCTF</p>
                    <div className="vp-actions">
                      {isCnc &&
                        (isPrePortal ? (
                          <PrePortalLockedButton
                            label="3D Model"
                            icon={<Download size={16} />}
                            tip={lockedFilesTip}
                          />
                        ) : (
                          <button
                            type="button"
                            className="vp-btn"
                            disabled={busyPkg === pkg.id || !hasThreeDFile}
                            title={!hasThreeDFile ? 'No 3D model available for this package.' : undefined}
                            onClick={() => void onDownloadThreeDFile(pkg)}
                          >
                            <Download size={16} /> 3D Model
                          </button>
                        ))}
                      {!isCnc && (
                        <>
                          {isPrePortal ? (
                            <>
                              <PrePortalLockedButton label="CSV" icon={<Download size={16} />} tip={lockedFilesTip} />
                              <PrePortalLockedButton label="PDF" icon={<Printer size={16} />} tip={lockedFilesTip} />
                              <PrePortalLockedButton label="3D Model" icon={<Download size={16} />} tip={lockedFilesTip} />
                            </>
                          ) : perStructureDownloads ? null : (
                            <>
                              <button
                                type="button"
                                className="vp-btn"
                                disabled={busyPkg === pkg.id}
                                onClick={() => void onDownloadCsv(pkg)}
                              >
                                <Download size={16} /> CSV
                              </button>
                              <button
                                type="button"
                                className="vp-btn"
                                disabled={busyPkg === pkg.id}
                                onClick={() => void onDownloadPdf(pkg)}
                              >
                                <Printer size={16} /> PDF
                              </button>
                              <button
                                type="button"
                                className="vp-btn"
                                disabled={busyPkg === pkg.id || !hasThreeDFile}
                                title={!hasThreeDFile ? 'No 3D model available for this package.' : undefined}
                                onClick={() => void onDownloadThreeDFile(pkg)}
                              >
                                <Download size={16} /> 3D Model
                              </button>
                            </>
                          )}
                        </>
                      )}
                      {canSeeFinancials && !isReferencePkg && (
                      <button
                        type="button"
                        className="vp-btn vp-btn-muted"
                        disabled={busyPkg === pkg.id || readOnlyPreview || hasLostAward}
                        onClick={() => void onNotBidding(pkg)}
                      >
                        <XCircle size={16} /> Not bidding
                      </button>
                      )}
                    </div>
                    {perStructureDownloads && (
                      <div className="vp-structure-downloads" data-tour="vp-timber-structure-downloads">
                        <p className="vp-structure-downloads-head">
                          Timber list by structure
                          <span className="vp-muted"> · {structureSlots.length} structures</span>
                        </p>
                        <ul className="vp-structure-download-list" aria-label="Timber list per structure">
                          {structureSlots.map(slot => {
                            const speciesLabel = speciesLabelFor(slot.structure);
                            return (
                              <li key={slot.structure.id} className="vp-structure-download">
                                <span className="vp-structure-download-name">
                                  {slot.structure.name || slot.structure.id}
                                  {speciesLabel && (
                                    <span className={`vp-structure-download-species${speciesLabel === 'Not priced' ? ' vp-structure-download-species--none' : ''}`}>
                                      {speciesLabel}
                                    </span>
                                  )}
                                </span>
                                <span className="vp-structure-download-actions">
                                  <button
                                    type="button"
                                    className="vp-btn vp-btn-sm"
                                    disabled={busyPkg === pkg.id}
                                    aria-label={`Download CSV for ${slot.structure.name || slot.structure.id}`}
                                    onClick={() => void onDownloadCsv(pkg, slot.structure)}
                                  >
                                    <Download size={14} /> CSV
                                  </button>
                                  <button
                                    type="button"
                                    className="vp-btn vp-btn-sm"
                                    disabled={busyPkg === pkg.id}
                                    aria-label={`Open PDF for ${slot.structure.name || slot.structure.id}`}
                                    onClick={() => void onDownloadPdf(pkg, slot.structure)}
                                  >
                                    <Printer size={14} /> PDF
                                  </button>
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="vp-action-group vp-action-group--supplier" data-tour={isCnc ? 'vp-cnc-supplier' : 'vp-timber-supplier'}>
                    <p className="vp-action-group-label">From {supplierOrgName}</p>
                    {isReferencePkg && (
                      <p className="vp-reference-no-quote">
                        No quote needed for this version — it is for production only.
                      </p>
                    )}
                    {canSeeFinancials && !isReferencePkg && structureSlots.length === 0 && (
                    <div className="vp-actions">
                      <button
                        type="button"
                        className={`vp-btn primary${readOnlyPreview ? ' vp-btn--disabled' : ''}`}
                        disabled={busyPkg === pkg.id || readOnlyPreview || hasLostAward}
                        onClick={() => onUploadQuote(pkg)}
                      >
                        <FileUp size={16} /> Upload quote
                      </button>
                    </div>
                    )}
                    {canSeeFinancials && !isReferencePkg && structureSlots.length > 0 && (
                      <div className="vp-structure-slots" data-tour={isCnc ? 'vp-cnc-structure-slots' : 'vp-timber-structure-slots'}>
                        <p className="vp-structure-slots-head">
                          One {isCnc ? 'CNC' : 'timber'} quote per structure
                          <span className="vp-muted"> · {quotedSlotCount} of {structureSlots.length} quoted</span>
                        </p>
                        <ul className="vp-structure-slot-list" aria-label="Quote per structure">
                          {structureSlots.map(slot => (
                            <li key={slot.structure.id} className={`vp-structure-slot${slot.latest ? ' vp-structure-slot--quoted' : ''}`}>
                              <span className="vp-structure-slot-name">
                                {slot.structure.name || slot.structure.id}
                                {speciesLabelFor(slot.structure) && (
                                  <span className="vp-structure-slot-species">{speciesLabelFor(slot.structure)}</span>
                                )}
                              </span>
                              <span className="vp-structure-slot-status">
                                {slot.latest
                                  ? slot.quoteTotal != null
                                    ? `Quoted ${formatQuoteTotal(slot.quoteTotal)}`
                                    : 'Quoted'
                                  : 'Awaiting quote'}
                              </span>
                              <button
                                type="button"
                                className={`vp-btn vp-btn-sm${slot.latest ? '' : ' primary'}${readOnlyPreview ? ' vp-btn--disabled' : ''}`}
                                disabled={busyPkg === pkg.id || readOnlyPreview || hasLostAward}
                                onClick={() => onUploadQuote(pkg, slot.structure)}
                              >
                                <FileUp size={14} /> {slot.latest ? 'Re-quote' : 'Upload quote'}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {supplierQuoteEntries.length > 0 && (
                      <ul className="vp-supplier-quote-rows" aria-label="Uploaded quotes">
                        {supplierQuoteEntries.map(
                          ({
                            sub: rowSub,
                            priceLabel,
                            species,
                            drynessLabel,
                            structureScopeLabel,
                            previous: rowPrevious,
                            isPendingRevision,
                            showPreviousHistory,
                            canonicalApprovedId,
                          }) => {
                          const historyId = rowSub.id ?? rowSub.storagePath ?? '';
                          const historyOpen = expandedHistoryIds.has(historyId);
                          const rowApproved =
                            !!rowSub.id && rowSub.id === canonicalApprovedId;
                          const rowRejected = !rowApproved && isRfqQuoteRejected(rowSub);
                          return (
                              <li
                                key={rowSub.id ?? rowSub.storagePath}
                                className={`vp-supplier-quote-row${
                                  rowApproved ? ' vp-supplier-quote-row--approved' : ''
                                }${rowRejected ? ' vp-supplier-quote-row--rejected' : ''}${
                                  isPendingRevision ? ' vp-supplier-quote-row--pending-revision' : ''
                                }`}
                              >
                                <div className="vp-supplier-quote-row-main">
                                  {rowApproved && (
                                    <span className="vp-quote-badge vp-quote-badge--approved">
                                      <BadgeCheck size={11} aria-hidden />{' '}
                                      {isCnc
                                        ? cncApprovedLabel
                                        : timberSubmissionApprovedBadgeLabel(project, rowSub.id)}
                                    </span>
                                  )}
                                  {rowRejected && (
                                    <span className="vp-quote-badge vp-quote-badge--rejected">
                                      Rejected
                                    </span>
                                  )}
                                  {isPendingRevision && (
                                    <span className="vp-quote-badge vp-quote-badge--pending-revision">
                                      {PENDING_REVISION_HINT}
                                    </span>
                                  )}
                                  {canSeeFinancials && priceLabel && priceLabel !== '—' && (
                                    <span className="vp-supplier-quote-row-price">{priceLabel}</span>
                                  )}
                                  {species && (
                                    <span className="vp-quote-badge vp-quote-badge--species">{species}</span>
                                  )}
                                  {structureScopeLabel && (
                                    <span className="vp-quote-badge">{structureScopeLabel}</span>
                                  )}
                                  {drynessLabel && (
                                    <span className="vp-quote-badge vp-quote-badge--dryness">{drynessLabel}</span>
                                  )}
                                  {formatSubmissionLeadTimeWeeks(rowSub) && (
                                    <span className="vp-quote-badge vp-quote-badge--lead-time">
                                      {formatSubmissionLeadTimeWeeks(rowSub)}
                                    </span>
                                  )}
                                  {rowSub.notes?.trim() && (
                                    <span className="vp-muted vp-supplier-quote-row-notes">{rowSub.notes.trim()}</span>
                                  )}
                                  {rowSub.createdAt instanceof Date && (
                                    <span className="vp-muted vp-supplier-quote-row-date">
                                      {rowSub.createdAt.toLocaleDateString(undefined, {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                      })}
                                    </span>
                                  )}
                                </div>
                                <div className="vp-supplier-quote-row-actions">
                                  {canSeeFinancials && (
                                    <button
                                      type="button"
                                      className="vp-btn vp-btn-sm"
                                      onClick={() => void onViewSubmission(rowSub)}
                                    >
                                      <Eye size={14} /> View
                                    </button>
                                  )}
                                  {canSeeFinancials && userId && rowSub.uploadedByUid === userId && rowSub.id && !rowRejected && (
                                    <button
                                      type="button"
                                      className="vp-link-btn vp-link-danger"
                                      onClick={() => void onDeleteSubmission(pkg, rowSub)}
                                    >
                                      <Trash2 size={12} style={{ verticalAlign: 'middle' }} /> Remove
                                    </button>
                                  )}
                                </div>
                                {showPreviousHistory && rowPrevious.length > 0 && (
                                  <div className="vp-quote-history">
                                    <button
                                      type="button"
                                      className="vp-link-btn vp-quote-history-toggle"
                                      aria-expanded={historyOpen}
                                      onClick={() => toggleHistory(historyId)}
                                    >
                                      {historyOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                      {rowPrevious.length === 1
                                        ? '1 previous quote'
                                        : `${rowPrevious.length} previous quotes`}
                                    </button>
                                    {historyOpen && (
                                      <ul className="vp-quote-history-list" aria-label="Previous quotes">
                                        {rowPrevious.map(prev => (
                                          <li key={prev.id ?? prev.storagePath} className="vp-quote-history-row">
                                            {canSeeFinancials && (
                                            <span className="vp-quote-history-price">
                                              {isCnc && isQuotePlaceholderSubmission(prev)
                                                ? CNC_QUOTE_PLACEHOLDER_ON_FILE_LABEL
                                                : formatQuoteTotal(submissionQuoteTotal(prev))}
                                            </span>
                                            )}
                                            {!isCnc && !!prev.id && approvedSubmissionIds.has(prev.id) && (
                                              <span className="vp-quote-badge vp-quote-badge--approved">
                                                <BadgeCheck size={11} aria-hidden />{' '}
                                                {timberSubmissionApprovedBadgeLabel(project, prev.id)}
                                              </span>
                                            )}
                                            {formatSubmissionLeadTimeWeeks(prev) && (
                                              <span className="vp-quote-badge vp-quote-badge--lead-time">
                                                {formatSubmissionLeadTimeWeeks(prev)}
                                              </span>
                                            )}
                                            {prev.notes?.trim() && (
                                              <span className="vp-muted vp-supplier-quote-row-notes">
                                                {prev.notes.trim()}
                                              </span>
                                            )}
                                            {prev.createdAt instanceof Date && (
                                              <span className="vp-muted">
                                                {prev.createdAt.toLocaleDateString(undefined, {
                                                  month: 'short',
                                                  day: 'numeric',
                                                  year: 'numeric',
                                                })}
                                              </span>
                                            )}
                                            {canSeeFinancials && prev.storagePath && (
                                              <button
                                                type="button"
                                                className="vp-link-btn"
                                                onClick={() => void onViewSubmission(prev)}
                                              >
                                                View
                                              </button>
                                            )}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                )}
                              </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              {!isCnc && latestSub?.notBidding && timberQuoteHeadings.length === 0 && (
                <div className="vp-quote-meta">
                  <span className="vp-quote-meta-status">You marked this package as not bidding.</span>
                  {latestSub.createdAt instanceof Date && (
                    <span className="vp-muted vp-quote-meta-date">
                      {latestSub.createdAt.toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                  )}
                  {userId && latestSub.uploadedByUid === userId && latestSub.id && (
                    <button
                      type="button"
                      className="vp-link-btn vp-link-danger"
                      onClick={() => void onDeleteSubmission(pkg, latestSub)}
                    >
                      <Trash2 size={12} style={{ verticalAlign: 'middle' }} /> Remove not bidding
                    </button>
                  )}
                </div>
              )}

              {isCnc && latestSub?.notBidding && (
                <div className="vp-quote-meta">
                  <span className="vp-quote-meta-status">You marked this package as not bidding.</span>
                  {latestSub.createdAt instanceof Date && (
                    <span className="vp-muted vp-quote-meta-date">
                      {latestSub.createdAt.toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                  )}
                  {userId && latestSub.uploadedByUid === userId && latestSub.id && (
                    <button
                      type="button"
                      className="vp-link-btn vp-link-danger"
                      onClick={() => void onDeleteSubmission(pkg, latestSub)}
                    >
                      <Trash2 size={12} style={{ verticalAlign: 'middle' }} /> Remove not bidding
                    </button>
                  )}
                </div>
              )}

            </div>
          </div>
        </li>
      </ul>
    </section>
  );
};

export default VendorPortalWorkPanel;
